import api from '../../api'

// Phase 1 contract — keep co-located until a `lvbl_web/src/types/Court.ts` is established.
export type CourtType = 'pickle' | 'tennis' | 'padel'

export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6

export type CourtOperatingHours = {
  day_of_week: DayOfWeek
  open_time: string | null
  close_time: string | null
}

export type Court = {
  id: string
  venue: string
  name: string
  court_type: CourtType
  available_for_private_lessons: boolean
  operating_hours: CourtOperatingHours[]
  pending_approval?: boolean
}

export type CourtCreatePayload = {
  name: string
  court_type: CourtType
  available_for_private_lessons: boolean
  operating_hours: CourtOperatingHours[]
}

export type CourtPricingRow = {
  court_type: CourtType
  hourly_rate_cents: number
  currency: string
}

export type CourtPricing = {
  venue: string
  pricing: CourtPricingRow[]
}

export type CourtPricingPayload = {
  pricing: CourtPricingRow[]
}

export const COURT_TYPE_LABEL: Record<CourtType, string> = {
  pickle: 'Pickleball',
  tennis: 'Tennis',
  padel: 'Padel',
}

export const formatCourtOptionLabel = (court: Pick<Court, 'name' | 'court_type'>) =>
  COURT_TYPE_LABEL[court.court_type] ? `${court.name} — ${COURT_TYPE_LABEL[court.court_type]}` : court.name

const normalizeCourtsResponse = (payload: unknown): Court[] => {
  if (Array.isArray(payload)) {
    return payload as Court[]
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  const maybeCourts = (payload as { courts?: unknown }).courts
  if (Array.isArray(maybeCourts)) {
    return maybeCourts as Court[]
  }

  const maybeData = (payload as { data?: unknown }).data
  if (Array.isArray(maybeData)) {
    return maybeData as Court[]
  }

  return []
}

// Mirrors the backend `venues.court_hours.enforce_operating_hours` gate: an
// event/lesson time must fall inside ONE of the court's open intervals for that
// weekday. A court with NO configured hours is treated as unrestricted
// (back-compat); a weekday with rows-but-none-matching is closed. `date` is the
// event's venue-local calendar date and `startFrac`/`endFrac` are venue-local
// fractional hours (e.g. 19.5 = 19:30) — the same wall-clock the create payload
// sends, so no timezone conversion is needed.
export const isWithinCourtHours = (
  court: Court | undefined,
  date: Date | null,
  startFrac: number,
  endFrac: number,
): boolean => {
  if (!court || !date) return true
  const rows = court.operating_hours ?? []
  if (rows.length === 0) return true // unconfigured court → unrestricted
  // JS Date.getDay(): 0=Sun..6=Sat. Backend day_of_week: 0=Mon..6=Sun.
  const dow = (((date.getDay() + 6) % 7) as DayOfWeek)
  const toFrac = (t: string) => {
    const [h, m] = t.split(':').map(Number)
    return h + (m || 0) / 60
  }
  return rows
    .filter((r) => r.day_of_week === dow && r.open_time && r.close_time)
    .some(
      (r) =>
        startFrac >= toFrac(r.open_time as string) &&
        endFrac <= toFrac(r.close_time as string),
    )
}

export const getCourts = async ({ venueId }: { venueId: string }) => {
  const res = await api.get(`venues/${venueId}/courts/`)
  return normalizeCourtsResponse(res.data)
}

export const postCourt = async ({ venueId, payload }: { venueId: string; payload: CourtCreatePayload }) => {
  const res = await api.post(`venues/${venueId}/courts/`, payload)
  return res.data as Court
}

export const patchCourt = async ({
  venueId,
  courtId,
  payload,
}: {
  venueId: string
  courtId: string
  payload: Partial<CourtCreatePayload>
}) => {
  const res = await api.patch(`venues/${venueId}/courts/${courtId}/`, payload)
  return res.data as Court
}

export const deleteCourt = async ({
  venueId,
  courtId,
  reassignTo,
}: {
  venueId: string
  courtId: string
  reassignTo?: string
}) => {
  const url = reassignTo
    ? `venues/${venueId}/courts/${courtId}/?reassign_to=${reassignTo}`
    : `venues/${venueId}/courts/${courtId}/`
  const res = await api.delete(url)
  return res.data
}

export const getCourtPricing = async ({ venueId }: { venueId: string }) => {
  const res = await api.get(`venues/${venueId}/court_pricing/`)
  return res.data as CourtPricing
}

export const putCourtPricing = async ({ venueId, payload }: { venueId: string; payload: CourtPricingPayload }) => {
  const res = await api.put(`venues/${venueId}/court_pricing/`, payload)
  return res.data as CourtPricing
}
